import React from 'react';
// @ts-ignore
import { useContentToken, EditableToken } from '@vibesites/runtime/react';

const FOOTER_NAV = [
  { href: '/apartments/', label: 'Apartments' },
  { href: '/about-us/', label: 'About Us' },
  { href: '/careers/', label: 'Careers' },
  { href: '/blog/', label: 'Blog' },
  { href: '/awards/', label: 'Awards' },
  { href: 'https://www.caseusacommercial.com/', label: 'Commercial' },
  { href: '/contact-us/', label: 'Contact Us' },
  { href: 'https://portal.office.com', label: 'Employee Login' },
];

const FacebookIcon = () => (
  <svg viewBox="0 0 448 512" width="24" height="24" fill="currentColor"><path d="M400 32H48A48 48 0 0 0 0 80v352a48 48 0 0 0 48 48h137.25V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.27c-30.81 0-40.42 19.12-40.42 38.73V256h68.78l-11 71.69h-57.78V480H400a48 48 0 0 0 48-48V80a48 48 0 0 0-48-48z"/></svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 448 512" width="24" height="24" fill="currentColor"><path d="M224,202.66A53.34,53.34,0,1,0,277.34,256,53.38,53.38,0,0,0,224,202.66Zm124.71-41a60,60,0,0,0-33.81-33.81C293.9,119.82,256,119.87,224,119.87s-69.9-.05-90.89,8C113.39,134.69,104.69,143.39,97.86,163.1c-8,21-7.95,58.88-7.95,90.9s-.05,69.9,8,90.89a60,60,0,0,0,33.81,33.81c21,8.05,58.88,8,90.89,8s69.9.05,90.89-8a60,60,0,0,0,33.81-33.81c8.05-21,8-58.88,8-90.89S356.76,183.67,348.71,161.68ZM224,338a82,82,0,1,1,82-82A81.9,81.9,0,0,1,224,338Zm85.38-148.3a19.14,19.14,0,1,1,19.13-19.14A19.1,19.1,0,0,1,309.38,189.74ZM400,32H48A48,48,0,0,0,0,80V432a48,48,0,0,0,48,48H400a48,48,0,0,0,48-48V80A48,48,0,0,0,400,32ZM382.88,322c-1.29,25.63-7.14,48.34-25.85,67s-41.4,24.63-67,25.85c-26.41,1.49-105.59,1.49-132,0-25.63-1.29-48.26-7.15-67-25.85s-24.63-41.42-25.85-67c-1.49-26.42-1.49-105.61,0-132,1.29-25.63,7.07-48.34,25.85-67s41.47-24.56,67-25.78c26.41-1.49,105.59-1.49,132,0,25.63,1.29,48.33,7.15,67,25.85s24.63,41.42,25.85,67.05C384.37,216.44,384.37,295.56,382.88,322Z"/></svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 448 512" width="24" height="24" fill="currentColor"><path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/></svg>
);

export function CorporateFooter() {
  const logo = useContentToken('footer.company_logo') as string;
  const logoAlt = useContentToken('footer.company_logo_alt', {
    defaultValue: 'Case and Associates logo',
  }) as string;
  const companyName = useContentToken('footer.company_name', {
    defaultValue: 'Case & Associates Properties, Inc.',
  });
  const address = useContentToken('footer.address', {
    defaultValue: '4200 E. Skelly Dr. Suite 800, Tulsa, OK 74135',
  });
  const copyright = useContentToken('footer.copyright', {
    defaultValue: `Copyright © ${new Date().getFullYear()} Case and Associates`,
  });
  const facebookUrl = useContentToken('footer.facebook_url', {
    defaultValue: 'https://www.facebook.com/CaseAndAssociates',
  }) as string;
  const instagramUrl = useContentToken('footer.instagram_url', {
    defaultValue: 'https://www.instagram.com/caseandassociates_/',
  }) as string;
  const tiktokUrl = useContentToken('footer.tiktok_url', {
    defaultValue: 'https://www.tiktok.com/@caseandassociates',
  }) as string;

  const socials = [
    { url: facebookUrl, label: 'Case & Associates Properties, Inc. Facebook link', icon: <FacebookIcon /> },
    { url: instagramUrl, label: 'Case & Associates Properties, Inc. Instagram link', icon: <InstagramIcon /> },
    { url: tiktokUrl, label: 'Case & Associates Properties, Inc. Tiktok link', icon: <TikTokIcon /> },
  ].filter(s => s.url);

  return (
    <footer className="case-footer">
      <div className="case-container" style={{ maxWidth: 1140 }}>
        <div className="case-footer-top">
          <a href="/" className="case-footer-logo">
            <EditableToken path="footer.company_logo" type="image" label="Company Logo">
              <img
                src={logo}
                alt={logoAlt}
                width="280"
                height="74"
                style={{ width: 280, height: 74, objectFit: 'contain' }}
              />
            </EditableToken>
          </a>
          <div className="case-footer-info">
            <address>
              <EditableToken path="footer.company_name" as="span">
                {companyName}
              </EditableToken><br />
              <EditableToken path="footer.address" as="span">
                {address}
              </EditableToken>
            </address>
            <EditableToken path="footer.copyright" as="span">
              {copyright}
            </EditableToken>
          </div>
        </div>

        <ul className="case-footer-nav">
          {FOOTER_NAV.map(l => (
            <li key={l.href}><a href={l.href}>{l.label}</a></li>
          ))}
        </ul>

        <div className="case-footer-socials">
          {socials.map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="socials_item" style={{ textDecoration: 'none' }}>
              <span className="sr-only">{s.label}</span>
              {s.icon}
            </a>
          ))}
        </div>

        <ul className="case-footer-policy">
          <li><a href="/privacy-policy">Privacy Policy</a></li>
          <li><a href="/accessibility-statement">Accessibility Statement</a></li>
        </ul>
      </div>
    </footer>
  );
}

export default CorporateFooter;
